/**
 * Memorelo cart drawer — gift wrap toggle.
 * Toggles a gift-wrap line item (a real Shopify product) in the cart via Ajax.
 *
 * Wiring:
 *  - Theme settings → gift_wrap_variant_id (saved to window.theme.giftWrapVariantId)
 *  - Drawer renders the checkbox via Liquid only when that setting is non-blank
 *  - Drawer JSON updates on success → cart-drawer.js re-renders the line items list
 */
(function () {
  'use strict';

  const SELECTORS = {
    container: '[data-gift-wrap]',
    toggle: '[data-gift-wrap-toggle]',
    price: '[data-gift-wrap-price]',
  };

  function getCheckbox() {
    return document.querySelector(SELECTORS.toggle);
  }

  function getContainer() {
    return document.querySelector(SELECTORS.container);
  }

  function setLoading(isLoading) {
    const container = getContainer();
    if (!container) return;
    container.classList.toggle('is-loading', isLoading);
  }

  function refreshCartDrawer() {
    // Use Dawn's own cart-drawer-items.onCartUpdate() — it fetches `?section_id=cart-drawer`
    // and replaces `cart-drawer-items` + `.cart-drawer__footer` elements via .replaceWith().
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      cartDrawerItems.onCartUpdate();
    }
    // Also refresh the cart icon bubble in the header
    fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
      .then((res) => res.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = parsed.querySelector('.shopify-section');
        const current = document.getElementById('cart-icon-bubble');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
      })
      .catch(() => {});
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return Promise.resolve();
  }

  function addGiftWrap(variantId) {
    setLoading(true);
    return fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then((res) => res.json())
      .then(() => refreshCartDrawer())
      .finally(() => setLoading(false));
  }

  function removeGiftWrap(lineKey) {
    if (!lineKey) {
      // Fall back to fetching cart to find the line by variant id
      const variantId = Number(getContainer().dataset.variantId);
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          const match = cart.items.find((i) => i.variant_id === variantId);
          if (!match) return;
          return doRemove(match.key);
        });
    }
    return doRemove(lineKey);
  }

  function doRemove(lineKey) {
    setLoading(true);
    return fetch(`${window.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    })
      .then((res) => res.json())
      .then(() => refreshCartDrawer())
      .finally(() => setLoading(false));
  }

  function attachToggleHandler() {
    const checkbox = getCheckbox();
    if (!checkbox || checkbox.dataset.giftWrapBound === 'true') return;
    checkbox.dataset.giftWrapBound = 'true';

    checkbox.addEventListener('change', function () {
      const container = getContainer();
      const variantId = container.dataset.variantId;
      if (checkbox.checked) {
        addGiftWrap(variantId).catch((err) => {
          console.error('[gift-wrap] add failed', err);
          checkbox.checked = false;
        });
      } else {
        const lineKey = container.dataset.lineKey;
        removeGiftWrap(lineKey).catch((err) => {
          console.error('[gift-wrap] remove failed', err);
          checkbox.checked = true;
        });
      }
    });
  }

  function showPrice() {
    const container = getContainer();
    const priceEl = container && container.querySelector(SELECTORS.price);
    if (!priceEl || priceEl.dataset.populated === 'true') return;
    const variantId = container.dataset.variantId;
    if (!variantId) return;

    fetch(`/variants/${variantId}.js`, { headers: { 'Accept': 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((variant) => {
        if (!variant) return;
        // Format using Shopify's locale-aware money — fall back to plain decimal
        const formatted = window.Shopify && Shopify.formatMoney
          ? Shopify.formatMoney(variant.price, window.Shopify.money_format || '€{{amount}}')
          : `${(variant.price / 100).toFixed(2)}`;
        priceEl.textContent = `+${formatted}`;
        priceEl.dataset.populated = 'true';
      })
      .catch(() => {});
  }

  function init() {
    attachToggleHandler();
    showPrice();
  }

  // Initial wire-up
  document.addEventListener('DOMContentLoaded', init);

  // Re-bind after cart drawer re-renders
  document.addEventListener('cart:refresh', init);
  document.addEventListener('cart:updated', init);

  // MutationObserver as a safety net for drawer DOM swaps
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    const observer = new MutationObserver(() => init());
    observer.observe(drawer, { childList: true, subtree: true });
  }
})();
